import { setSyncProgress, store } from '@/app/store';
import { formatUserIdToJid } from '@/features/chats';
import {
   appendRecentChats,
   setRecentChatHasMore,
   setRecentChats,
   setRecentChatsLoading,
} from '@/features/chats/model/recentChat';
import { getRecentChatsByPagination } from '@/infrastructure/sdk';
import { createLogger, yieldToMain } from '@/infrastructure/sync/utils';
import { SYNC_CONSTANTS } from '@/shared/constants';
import storage from '@/shared/utils/storage';
import { syncGroupParticipants } from './participants';
import { syncUserProfiles } from './profiles';
// ============================================================================
// CONFIGURATION & CONSTANTS
// ============================================================================

const { RECENT_CHAT_FETCH_LIMIT } = SYNC_CONSTANTS;
const { log, logTime, flushLogs } = createLogger('ChatSync');

const updateLastSyncTime = time => {
   storage.setItem('lastSyncTime', time || Date.now());
};

const reportProgress = (value, enabled) => {
   if (!enabled) return;
   store.dispatch(setSyncProgress(Math.floor(value)));
};

/**
 * MODULE: ChatSync
 * Manages recent chat data flow between API and Redux (Redux-only, no IDB).
 */
export const ChatSync = {
   async enrichChatData(chats) {
      if (!chats?.length) return;

      const userIds = [...new Set(chats.map(item => item.fromUserId).filter(Boolean))];
      //Sync Prodiles
      if (userIds.length > 0) {
         await syncUserProfiles(userIds, { isSyncCheckNeeded: false });
      }

      const groupChats = chats.filter(item => item.chatType === 'groupchat');
      if (groupChats.length > 0) {
         const uniqueGroupsMap = new Map();
         groupChats.forEach(item => {
            if (item.fromUserId && !uniqueGroupsMap.has(item.fromUserId)) {
               uniqueGroupsMap.set(item.fromUserId, {
                  groupId: item.fromUserId,
                  groupJid: formatUserIdToJid(item.fromUserId, item.chatType),
               });
            }
         });

         const groupsToSync = Array.from(uniqueGroupsMap.values());
         if (groupsToSync.length > 0) {
            await syncGroupParticipants(groupsToSync, { enableProgress: false });
         }
      }
   },

   async syncToRedux({ chats, hasMore = true, messageTime = 0 }) {
      if (!chats?.length) {
         store.dispatch(setRecentChatHasMore(false));
         return;
      }
      if (messageTime) {
         store.dispatch(appendRecentChats(chats));
      } else {
         store.dispatch(setRecentChats(chats));
      }
      store.dispatch(setRecentChatHasMore(hasMore));
   },

   async processBatch(chats, options = {}) {
      const { hasMore = false, messageTime } = options;
      if (!chats?.length) {
         await this.syncToRedux({ chats: [], hasMore: false });
         return;
      }

      await this.enrichChatData(chats);
      await this.syncToRedux({ chats, hasMore, messageTime });
   },

   async getRecentChats({
      messageTime = 0,
      limit = RECENT_CHAT_FETCH_LIMIT,
      direction = 'backward',
      fetchDelay = 0,
   } = {}) {
      const dispatch = store.dispatch;
      dispatch(setRecentChatsLoading(true));

      try {
         if (fetchDelay > 0) await new Promise(resolve => setTimeout(resolve, fetchDelay));

         log('Get', 'Fetching from API');
         const { chats: apiChats = [], hasMore: apiHasMore } = await fetchChatsWithPagination(
            { messageTime, limit, direction },
            null,
            true,
         );
         await this.processBatch(apiChats, { hasMore: apiHasMore, messageTime });

         return apiChats;
      } catch (err) {
         log('Error', `getRecentChats failed: ${err.message}`);
         return [];
      } finally {
         dispatch(setRecentChatsLoading(false));
      }
   },
};

async function fetchChatsWithPagination(
   { direction = 'backward', messageTime = null, limit = RECENT_CHAT_FETCH_LIMIT },
   onProgress,
   isSinglePage = false,
) {
   const allChats = [];
   let currentMessageTime = messageTime;
   let batchCount = 0;
   let lastBatchSize = 0;

   while (true) {
      batchCount++;
      const response = await getRecentChatsByPagination({ limit, direction, messageTime: currentMessageTime });

      if (response?.statusCode !== 200 || !response.data?.length) {
         lastBatchSize = 0;
         break;
      }

      const batchData = response.data;
      allChats.push(...batchData);
      lastBatchSize = batchData.length;

      if (onProgress) onProgress(Math.min(batchCount * 0.1, 0.9));
      if (isSinglePage || batchData.length < limit) break;

      currentMessageTime = batchData[batchData.length - 1].messageTime;
      await yieldToMain();
   }

   return { chats: allChats, hasMore: lastBatchSize >= limit };
}

export const syncChats = async (options = {}) => {
   const { progressRange = [3, 10], config = {} } = options;
   const { preFetch, isSinglePage } = config;
   const [startProgress, endProgress] = progressRange;

   log('Sync', `Starting | preFetch=${preFetch}`);
   const startTime = performance.now();

   try {
      const { chats: apiChats, hasMore: apiHasMore } = await fetchChatsWithPagination(
         { direction: 'backward' },
         percent => reportProgress(startProgress + (endProgress - startProgress) * percent, preFetch),
         isSinglePage ?? true,
      );

      if (apiChats.length > 0) {
         await ChatSync.processBatch(apiChats, { hasMore: apiHasMore });
      }

      updateLastSyncTime();
      reportProgress(endProgress, preFetch);
      logTime('Summary', 'Sync complete', startTime);
      return { chats: apiChats, totalCount: apiChats.length };
   } catch (error) {
      log('Error', `Failed: ${error.message}`);
      reportProgress(endProgress, preFetch);
      store.dispatch(setRecentChatHasMore(false));
      return { chats: [], totalCount: 0, error: true };
   } finally {
      flushLogs(`[SyncChats] Finished in ${((performance.now() - startTime) / 1000).toFixed(2)}s`);
   }
};

export const syncReconnect = () => syncChats();
